import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import ValidationErrorText from "../inputFields/ValidationErrorText";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../hooks/useDebounce";

export const LazyLoadDropdown = ({
  name,
  control,
  placeholder,
  options = [],
  filteredOptions = [],
  loading,
  hasMore,
  onLoadMore,
  validationRules,
  onSearch,
}) => {
  const { t } = useTranslation();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    if (debouncedSearchQuery) {
      onSearch(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  const handleInputChange = (newValue) => {
    setSearchQuery(newValue);
  };

  const handleMenuScrollToBottom = () => {
    if (hasMore && !loading) {
      onLoadMore();
    }
  };

  return (
    <DropdownWrapper>
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field, fieldState: { error } }) => (
          <>
            <CustomSelect
              {...field}
              options={searchQuery ? filteredOptions : options}
              isSearchable={true}
              placeholder={placeholder}
              onMenuScrollToBottom={handleMenuScrollToBottom}
              isLoading={loading}
              menuIsOpen={menuIsOpen}
              onMenuOpen={() => setMenuIsOpen(true)}
              onMenuClose={() => setMenuIsOpen(false)}
              classNamePrefix="react-select"
              onInputChange={handleInputChange}
              onChange={(selectedOption) => {
                field.onChange(selectedOption ? selectedOption.value : null);
              }}
              value={options && options.length > 0 && options.find(
                (option) => option.value === field.value
              )}
              styles={{
                control: (base) => ({
                  ...base,
                  border: error ? "1px solid red" : base.border,
                }),
              }}
            />
            {error && (
              <ValidationErrorText
                text={
                  error.type === "required"
                    ? t("Pleaseselectone")
                    : error.message
                }
              />
            )}
          </>
        )}
      />
    </DropdownWrapper>
  );
};

const DropdownWrapper = styled.div`
  width: 100%;
  .css-1u9des2-indicatorSeparator {
    display: none;
  }
`;

const CustomSelect = styled(Select)`
  .react-select__control {
    border: 1px solid ${({ border }) => (border ? border : "var(--border)")};
    border-radius: 8px;
    height: 48px;
    width: 100%;
    background: ${({ background }) =>
      background ? background : "var(--bgSecondary)"};
    box-shadow: none;

    &:hover {
      border-color: var(--border);
    }
  }

  .react-select__control--is-focused {
    border-color: var(--themeColor);
    box-shadow: none;
  }

  .react-select__value-container {
    padding-left: 10px;
    color: var(--text);
  }

  .react-select__placeholder {
    color: var(--text2);
  }

  .react-select__single-value {
    color: var(--text);
  }

  .react-select__menu {
    background: var(--bgSecondary);
    color: var(--text);
  }

  .react-select__option {
    background: var(--bgSecondary);
    color: var(--text);
    cursor: pointer;

    &:hover {
      background: var(--hoverColor);
    }

    &.react-select__option--is-selected {
      background: var(--themeColor);
      color: white;
    }
  }
`;
